import { useEffect, useMemo, useState } from "react";
import Select from "../../../components/select";
import arrow from "../../../assets/svg-icons/blueArrow.svg";
import close from "../../../assets/svg-icons/closeBrand.svg";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useFloatingIPsList, usePortList, useServerList } from "../layout/resuableEffect";
import { FormatDate, showErrorMessage } from "../../../hooks/constants";
import { useFormik } from "formik";
import * as Yup from "yup";
import Button from "../../../components/button";
import TextArea from "../../../components/textArea";
import { allocateProjectFloatingIPs, deleteFloatingIp, listProjectFloatingIPs, updateFloatingIp } from "../../../hooks/local/userReducer";
import Modal from "../../../components/modal";
import { toast } from "react-toastify";
import InlineSpinner from "../../../components/buttonSpinner";
import PaymentModal from "../../../components/paymentModal";



const FloatingIPS = () => {


  document.title = "Floating IPS - Cloudvantage";

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchText, setSearchText] = useState("");
  const projectId = useSelector((state) => state.user.selectedProject).id;

  const currency = useSelector((state) => state.user.currency);
  const walletBalance = useSelector((state) =>
    state.user.virtualAccount
      ? currency === "NGN"
        ? state.user.virtualAccount.balance
        : state.user.virtualAccount.dollar_balance
      : 0
  ); // Retrieve wallet balance
  const virtualAccount = useSelector((state) => state.user.virtualAccount);


  const [networkModal, setNetworkModal] = useState(false);
  const [manageFloatingIpModal, setManageFloatingIpModal] = useState(false);
  const [selectedFloatingIP, setSelectedFloatingIP] = useState(null);
  const [floatingIPModal, setFloatingIPModal] = useState(false);
  const [isDeassociate, setIsDeassociate] = useState(false);
  const [selectedAssociateFloatingIP, setSelectedAssociateFloatingIP] = useState(null);
  const [selectedRows, setSelectedRows] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const loading = useSelector((state) => state.user.loading);

  const floatingIPList = useFloatingIPsList();

  const params = "?device_owner=compute:nova";
  const portList = usePortList(projectId, params);

  // Helper function to get all associated floating IPs
  const getAssociatedFloatingIPs = () => {
    const associatedIPs = new Set();
    floatingIPList.forEach((ip) => {
      if (ip.port_id && ip.fixed_ip_address) {
        associatedIPs.add(ip.floating_ip_address);
      }
    });
    return associatedIPs;
  };

  // Helper function to get the port for a server or load balancer based on port_id
  const getServerPort = (floatingIP, portList) => {
    const matchingPort = portList.find((port) => port.id === floatingIP.port_id);

    return matchingPort
      ? {
        value: matchingPort.id,
        label: `${matchingPort.device_name || "Load Balancer"}: ${matchingPort.fixed_ips[0].ip_address
          } (${matchingPort.device_owner === "compute:nova" ? "Instance" : "Load Balancer"})`,
      }
      : null;
  };

  // Update toggleFloatingIPModal to set the portId
  const toggleFloatingIPModal = (floatingIP) => {
    setFloatingIPModal(!floatingIPModal);
    setSelectedAssociateFloatingIP(floatingIP);
    setIsDeassociate(false);

    const matchingPort = getServerPort(floatingIP, portList);
    if (matchingPort) {
      manageFloatingIP.setFieldValue("portId", matchingPort.value);
    }

    manageFloatingIP.setFieldValue("ipAddress", floatingIP.id);
  };

  const toggleDeassociateFloatingIPModal = (floatingIP) => {
    setIsDeassociate(!isDeassociate);
    setFloatingIPModal(!floatingIPModal);
    setSelectedAssociateFloatingIP(floatingIP);

    manageFloatingIP.setFieldValue("ipAddress", floatingIP.id);
    manageFloatingIP.setFieldValue("portId", floatingIP.port_id || "");
  };

  const getPortOptions = (floatingIP) => {
    if (!floatingIP || isDeassociate) {
      return [{ label: "Select a port", value: "" }];
    }

    const matchingPort = getServerPort(floatingIP, portList);
    return [
      { label: "Select a port", value: "" },
      ...(matchingPort
        ? [matchingPort]
        : portList.map((port) => ({
          value: port.id,
          label: `${port.device_name || "Load Balancer"}: ${port.fixed_ips[0].ip_address
            } (${port.device_owner === "compute:nova" ? "Instance" : "Load Balancer"})`,
        }))
      ),
    ];
  };

  const floatingIPsOptions = Array.isArray(floatingIPList)
    ? [
      { label: "Select a floating ip", value: "" },
      ...floatingIPList.map((ip) => ({
        value: ip.id,
        label: ip.floating_ip_address,
      })),
    ]
    : [{ label: "Select a floating ip", value: "" }];

  const floatingIPListMap = Array.isArray(floatingIPList)
    ? floatingIPList.map((network) => ({
      ipAddress: network.floating_ip_address || "N/A",
      pool: "External",
      id: network.id,
      description: network?.description || "N/A",
      fixedIpAddress: network?.fixed_ip_address || "N/A",
      status: network.status,
      createdAt: FormatDate(network.created_at),
    }))
    : [];

  const showManageFloatingIpModal = (key, data) => {
    setManageFloatingIpModal(key);
    setSelectedFloatingIP(data);
  };

  const closeManageNetworkModal = () => {
    setManageFloatingIpModal(false);
  };

  const toggleNetworkModal = () => {
    setNetworkModal(!networkModal);
  };

  const handleSearchChange = (event) => {
    setSearchText(event.target.value);
    setCurrentPage(1);
  };

  const portListOptions = [
    { label: "Select a pool", value: "" },
    { label: "External", value: "external" },
  ];

  const options = [
    { value: "all-floating-ips", label: "All floating-ips" },
    { value: "ipAddress", label: "IP Address" },
    { value: "status", label: "Status" },
  ];

  const [currentPage, setCurrentPage] = useState(1);
  const entriesPerPage = 5;

  const filteredData = floatingIPListMap?.filter(
    (row) =>
      row?.ipAddress?.toLowerCase().includes(searchText.toLowerCase()) ||
      row?.status?.toLowerCase().includes(searchText.toLowerCase())
  );

  const indexOfLastEntry = currentPage * entriesPerPage;
  const indexOfFirstEntry = indexOfLastEntry - entriesPerPage;
  const currentEntries = filteredData.slice(indexOfFirstEntry, indexOfLastEntry);
  const totalPages = Math.ceil(filteredData.length / entriesPerPage);

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const calculateEstimatedCost = (paymentMode) => {
    const IPAddressPrice = currency === "NGN" ? 3500 : 2.5;
    let totalCost = IPAddressPrice;

    if (paymentMode === "yearly") {
      const yearlyPrice = totalCost * 12;
      totalCost = yearlyPrice;
    }

    return totalCost; // Return numeric value for comparison
  };

  const createAllocateIPs = useFormik({
    initialValues: {
      portId: "",
      paymentMode: "monthly",
      description: "",
    },
    validationSchema: Yup.object({
      portId: Yup.string().required("Pool is required"),
      paymentMode: Yup.string().required("Payment Mode is required"),
    }),
    onSubmit: async (values) => {
      try {
        await createAllocateIPs.validateForm();
        const errors = createAllocateIPs.errors;
        const touched = createAllocateIPs.touched;

        if (Object.keys(errors).length > 0) {
          Object.keys(errors).forEach((field) => {
            if (touched[field]) {
              toast.error(`${field}: ${errors[field]}`);
            }
          });
          return;
        }

        const { description, paymentMode } = values;
        const totalCost = calculateEstimatedCost(paymentMode);

        if (totalCost > walletBalance) {
          setShowPaymentModal(true); // Show PaymentModal if cost exceeds balance
          return;
        }

        const floatingIPsData = {
          projectId,
          payment_mode: paymentMode,
        };

        const { payload } = await dispatch(allocateProjectFloatingIPs(floatingIPsData));
        if (payload.statuscode === "00") {
          await dispatch(listProjectFloatingIPs(projectId));
          toast.success("Floating IP allocated successfully");
          setNetworkModal(false);
          window.location.reload();
        } else {
          showErrorMessage(payload);
        }
      } catch (error) {
        console.error(error);
        showErrorMessage(error);
      }
    },
  });

  const [calculatedEstimate, setCalculatedEstimate] = useState("0.00");

  useEffect(() => {
    const estimate = calculateEstimatedCost(createAllocateIPs?.values?.paymentMode).toLocaleString(
      "en-US",
      {
        style: "currency",
        currency: currency === "NGN" ? "NGN" : "USD",
      }
    );
    setCalculatedEstimate(estimate);
  }, [createAllocateIPs?.values?.paymentMode, currency]);

  const manageFloatingIP = useFormik({
    initialValues: {
      ipAddress: "",
      portId: "",
      description: "",
    },
    validationSchema: Yup.object({
      ipAddress: Yup.string().required("IP address is required"),
      portId: isDeassociate ? Yup.string().optional() : Yup.string().required("Port is required"),
    }),
    onSubmit: async (values) => {
      const { ipAddress, portId, description } = values;

      let floatingIpData;
      if (isDeassociate) {
        floatingIpData = {
          projectId,
          floatingIpId: ipAddress,
        };
      } else {
        floatingIpData = {
          projectId,
          floatingIpId: ipAddress,
          port_id: portId,
        };
      }

      const { payload } = await dispatch(updateFloatingIp(floatingIpData));
      if (payload.statuscode === "00") {
        toast.success(isDeassociate ? "Floating IP disassociated successfully" : "Floating IP associated successfully");
        window.location.reload();
      } else {
        showErrorMessage(payload);
      }
    },
  });

  const handleAllocateIP = async () => {
    const totalCost = calculateEstimatedCost(createAllocateIPs.values.paymentMode);

    if (totalCost > walletBalance) {
      setShowPaymentModal(true); // Show PaymentModal if cost exceeds balance
      return;
    }

    const floatingIPsData = {
      projectId,
    };

    const { payload } = await dispatch(allocateProjectFloatingIPs(floatingIPsData));
    if (payload.statuscode === "00") {
      toast.success("Floating IP allocated successfully");
      window.location.reload();
    } else {
      showErrorMessage(payload);
    }
  };

  const handleServerAction = async (action, floatingIpId) => {
    const { payload } = await dispatch(action({ projectId, floatingIpId }));
    if (payload.statuscode === "00") {
      await dispatch(listProjectFloatingIPs({ projectId }));
      toast.success("Floating IP released successfully");
      window.location.reload();
    } else {
      showErrorMessage(payload);
    }
  };

  const deleteFloatingIP = (floatingIpId) =>
    handleServerAction(deleteFloatingIp, floatingIpId);

  const handleRowSelection = (id) => {
    setSelectedRows((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  };

  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedRows([]);
    } else {
      setSelectedRows(currentEntries.map((row) => row.id));
    }
    setSelectAll(!selectAll);
  };

  const handleReleaseFloatingIPs = async () => {
    if (selectedRows.length > 0) {
      const payInvoiceFormData = {
        floating_ips: selectedRows,
      };
      const { payload } = await dispatch(
        deleteFloatingIp({ payInvoiceFormData, navigate })
      );
      if (payload.statuscode === "00") {
        toast.success(payload.message);
        window.location.reload();
      } else {
        showErrorMessage(payload);
      }
    }
  };

  return (
    <div className="min-h-screen px-4 md:px-10 pt-6 pb-10 dark:text-white">
      <p className="crimson-font text-xl font-medium text-primaryBlue">
        Manage your floating IPs
      </p>

      {floatingIPListMap?.length > 0 ? (
        <div>
          <div className="w-full flex justify-between items-center gap-4 lg:flex-row flex-col mb-10">
            <div className="w-full grid grid-cols-1 md:grid-cols-12 gap-4 items-end my-10">
              <div className="w-full md:col-span-3">
                <Select label="Filter floating ips" name="floating ips" options={options} />
              </div>
              <div className="md:col-span-4">
                <input
                  type="search"
                  name=""
                  value={searchText}
                  onChange={handleSearchChange}
                  placeholder="Search for floating ip..."
                  className="bg-white text-black w-full p-4 text-sm rounded-md border border-[#c4c4c460] active:outline-0 focus:outline-0 placeholder:opacity-70 placeholder:text-xs dark:bg-slate-800 dark:text-white"
                />
              </div>
            </div>

            <div className="w-full md:w-[30%] flex justify-end items-center lg:flex-nowrap flex-wrap gap-4">
              <button
                className={`${floatingIPList?.length === 5 ? "cursor-not-allowed opacity-20" : "cursor-pointer"
                  } px-4 py-4 text-xs border-[1.5px] border-primaryBlue rounded-md text-primaryBlue w-full text-center`}
                onClick={toggleNetworkModal}
                disabled={floatingIPList?.length === 5}
              >
                Allocate IP To Project
              </button>
            </div>
          </div>

          <div className="overflow-x-auto rounded-md border dark:border-[#c4c4c432]">
            <table className="w-full text-sm text-left overflow-hidden">
              <thead className="bg-white dark:bg-slate-800 px-3 md:px-4 border text-gray-400 dark:border-[#c4c4c432] border-b text-sm font-semibold">
                <tr>
                  <th scope="col" className="px-6 py-4 whitespace-nowrap max-w-52">
                    #
                  </th>
                  <th scope="col" className="px-6 py-4 whitespace-nowrap max-w-52">
                    IP Address
                  </th>
                  <th scope="col" className="px-6 py-4 whitespace-nowrap">
                    Description
                  </th>
                  <th scope="col" className="px-6 py-4 whitespace-nowrap">
                    Mapped Fixed IP Address
                  </th>
                  <th scope="col" className="px-6 py-4 whitespace-nowrap">
                    Status
                  </th>
                  <th scope="col" className="px-6 py-4 whitespace-nowrap">
                    Pool
                  </th>
                  <th scope="col" className="px-6 py-4 whitespace-nowrap">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-slate-800 px-3 md:px-4 border rounded-b-md dark:border-[#c4c4c432] border-t-2 text-sm">
                {currentEntries.map((row, index) => (
                  <tr
                    key={index}
                    className="dark:hover:bg-[#ffffff05] hover:bg-brandBlue/25 whitespace-nowrap"
                  >
                    <td className="text-start py-4 px-6">{index + 1}.</td>
                    <td className="text-start py-4 px-6">{row.ipAddress}</td>
                    <td className="text-start py-4 px-6">{row.description}</td>
                    <td className="text-start py-4 px-6">{row.fixedIpAddress}</td>
                    <td className="text-start py-4 px-6">{row.status}</td>
                    <td className="text-start py-4 px-6">{row.pool}</td>
                    <td className="text-start py-4 px-6">
                      <div className="flex items-center gap-2">
                        {row.fixedIpAddress !== "N/A" ? (
                          <button
                            className="text-white p-3 text-xs rounded bg-primaryBlue w-full md:w-fit"
                            onClick={() => toggleDeassociateFloatingIPModal(row)}
                          >
                            Disassociate Floating IP
                          </button>
                        ) : (
                          <button
                            className="text-white p-3 text-xs rounded bg-primaryBlue w-full md:w-fit"
                            onClick={() => toggleFloatingIPModal(row)}
                          >
                            Associate Floating IP
                          </button>
                        )}
                        <button
                          className="text-white p-3 text-xs rounded bg-red-500 w-full md:w-fit"
                          onClick={() => showManageFloatingIpModal(index, row)}
                        >
                          Release Floating IP
                        </button>
                      </div>

                      {manageFloatingIpModal === index && (
                        <Modal
                          title={`Release Floating IP`}
                          isVisible={selectedFloatingIP}
                          onClose={() => setSelectedFloatingIP(null)}
                        >
                          <div className="text-sm text-center pt-6 text-wrap">
                            You have selected{" "}
                            <span className="underline">{selectedFloatingIP?.ipAddress}</span> .
                            Please confirm your selection. Once a floating IP is released, there is no guarantee the same IP can be allocated again.
                          </div>
                          <div className="grid gap-4 justify-center py-6">
                            <button
                              className="text-[#1DA1F2] text-sm px-6 py-2 hover:underline"
                              onClick={() => setSelectedFloatingIP(null)}
                            >
                              Cancel
                            </button>
                            <button
                              className="bg-red-500 text-white text-xs px-6 py-4 rounded-sm w-fit"
                              onClick={() => deleteFloatingIP(row.id)}
                            >
                              Yes, Release Floating IP
                            </button>
                          </div>
                        </Modal>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="grid justify-center gap-4 md:flex md:justify-between items-center mt-4">
            <p className="text-gray-400 text-sm">
              Showing{" "}
              <span className="font-medium text-black dark:text-white">
                {indexOfFirstEntry + 1}
              </span>{" "}
              to{" "}
              <span className="font-medium text-black dark:text-white">
                {Math.min(indexOfLastEntry, filteredData?.length)}
              </span>{" "}
              of{" "}
              <span className="font-medium text-black dark:text-white">
                {filteredData?.length}
              </span>{" "}
              results
            </p>
            <div className="flex gap-3">
              <button
                className={`bg-white dark:bg-primaryBlue border dark:border-none rounded-md py-3 px-5 text-sm ${currentPage === 1 ? "opacity-50" : "opacity-100"
                  }`}
                onClick={handlePreviousPage}
                disabled={currentPage === 1}
              >
                Previous
              </button>
              <button
                className={`bg-white dark:bg-primaryBlue border dark:border-none rounded-md py-3 px-5 text-sm ${currentPage === totalPages ? "opacity-50" : "opacity-100"
                  }`}
                onClick={handleNextPage}
                disabled={currentPage === totalPages}
              >
                Next
              </button>
            </div>
          </div>
        </div>
      ) : (
        <>
          <div className="md:px-52 text-center grid text-gray-500 mt-32">
            <div>
              It looks like you don't have any floating IPs yet. Once you allocate a floating IP to a project, your floating IPs details will appear here.
            </div>
            <div
              className="border border-[#c4c4c450] dark:border-[#c4c4c416] rounded-md p-3 flex items-center justify-self-center mt-8 w-fit text-xs cursor-pointer"
              onClick={toggleNetworkModal}
            >
              <span>Allocate IP To Project</span>
              <span className="ps-2">
                <img src={arrow} alt="" />
              </span>
            </div>
          </div>
        </>
      )}

      {/* Allocate IP Modal */}
      {networkModal && (
        <div className="fixed top-0 h-screen w-full left-0 z-50 flex items-center justify-center p-4 bg-black/40">
          <div className="bg-white dark:bg-[#141D26] rounded-md p-4 md:p-10 w-full md:w-1/3 max-h-[80%] overflow-y-scroll">
            <div className="flex items-center justify-between mb-4">
              <p className="text-primaryBlue">Allocate IP To Project</p>
              <span onClick={toggleNetworkModal} className="cursor-pointer">
                <img alt="" src={close} className="h-5" />
              </span>
            </div>
            <form className="gap-y-6 grid" onSubmit={createAllocateIPs.handleSubmit}>
              <Select
                label={"Pool"}
                type={"text"}
                name={"portId"}
                options={portListOptions}
                placeholder={"Select a pool"}
                value={createAllocateIPs.values.portId}
                onBlur={createAllocateIPs.handleBlur}
                onChange={createAllocateIPs.handleChange}
                onError={
                  createAllocateIPs.errors.portId && createAllocateIPs.touched.portId
                    ? createAllocateIPs.errors.portId
                    : null
                }
              />
              <Select
                label={"Payment Method"}
                type={"text"}
                name={"paymentMode"}
                options={[
                  { label: "Select payment method", value: "" },
                  { label: "Monthly", value: "monthly" },
                  { label: "Yearly", value: "yearly" },
                ]}
                placeholder={"Select payment method"}
                value={createAllocateIPs.values.paymentMode}
                onBlur={createAllocateIPs.handleBlur}
                onChange={createAllocateIPs.handleChange}
                onError={
                  createAllocateIPs.errors.paymentMode && createAllocateIPs.touched.paymentMode
                    ? createAllocateIPs.errors.paymentMode
                    : null
                }
              />
              <TextArea
                label={"Description"}
                type={"text"}
                name={"description"}
                value={createAllocateIPs.values.description}
                onBlur={createAllocateIPs.handleBlur}
                onChange={createAllocateIPs.handleChange}
                onError={
                  createAllocateIPs.errors.description && createAllocateIPs.touched.description
                    ? createAllocateIPs.errors.description
                    : null
                }
              />
              {createAllocateIPs.values.portId === "external" && createAllocateIPs.values.paymentMode && (
                <div className="grid gap-2">
                  <div className="text-xs">Estimated cost:</div>
                  <div className="font-semibold crimson-font text-primaryBlue text-lg">
                    {calculatedEstimate}
                  </div>
                </div>
              )}
              <div className="flex justify-between items-center gap-2">
                <Button
                  buttonText={
                    createAllocateIPs.values.portId === "external" &&
                      createAllocateIPs.values.paymentMode &&
                      calculateEstimatedCost(createAllocateIPs.values.paymentMode) > walletBalance
                      ? "Pay Now"
                      : "Allocate IP to Project"
                  }
                  buttonRole={"submit"}
                  disabled={loading}
                />
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Payment Modal */}
      <PaymentModal
        isOpen={showPaymentModal}
        onClose={() => setShowPaymentModal(false)}
        currency={currency}
        totalPrice={calculateEstimatedCost(createAllocateIPs.values.paymentMode)}
        walletBalance={walletBalance}
        virtualAccount={virtualAccount}
        onPaymentSuccess={async () => {
          const { description, paymentMode } = createAllocateIPs.values;
          const floatingIPsData = {
            projectId,
            payment_mode: paymentMode,
          };

          const { payload } = await dispatch(allocateProjectFloatingIPs(floatingIPsData));
          if (payload.statuscode === "00") {
            await dispatch(listProjectFloatingIPs(projectId));
            toast.success("Floating IP allocated successfully");
            setNetworkModal(false);
            setShowPaymentModal(false);
            window.location.reload();
          } else {
            showErrorMessage(payload);
          }
        }}
      />

      {/* Manage Floating IP Associations Modal */}
      {floatingIPModal && (
        <div className="fixed top-0 h-screen w-full left-0 z-50 bg-black/40 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-[#141D26] rounded-md p-4 w-full md:w-1/3 max-h-[80%] overflow-y-scroll">
            <div className="flex items-center justify-between mb-4">
              <p className="text-lg font-medium">
                {isDeassociate ? "Disassociate Floating IP" : "Associate Floating IP"}
              </p>
              <span
                onClick={isDeassociate ? toggleDeassociateFloatingIPModal : toggleFloatingIPModal}
                className="cursor-pointer"
              >
                <img alt="" src={close} className="h-5" />
              </span>
            </div>
            <form className="gap-y-6 grid" onSubmit={manageFloatingIP.handleSubmit}>
              <div className="grid gap-2">
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium">
                    IP Address <span className="text-red-500">*</span>
                  </label>
                  {floatingIPsOptions.length === 1 && !isDeassociate && floatingIPList?.length === 5 && (
                    <button
                      type="button"
                      onClick={handleAllocateIP}
                      className="text-primaryBlue text-sm flex items-center gap-1 underline"
                    >
                      <span className="text-lg">+</span> Allocate IP
                    </button>
                  )}
                </div>
                <p className="text-sm text-gray-500">
                  {isDeassociate
                    ? "Select the floating IP address to disassociate."
                    : "Select the IP address you wish to associate with the selected instance or load balancer port."}
                </p>
                <Select
                  name="ipAddress"
                  options={
                    isDeassociate
                      ? floatingIPsOptions.filter((option) =>
                        floatingIPList.some(
                          (ip) => ip.floating_ip_address === option.label && ip.port_id && ip.fixed_ip_address
                        )
                      )
                      : floatingIPsOptions.filter((option) => !getAssociatedFloatingIPs().has(option.label))
                  }
                  value={manageFloatingIP.values.ipAddress}
                  onBlur={manageFloatingIP.handleBlur}
                  onChange={(e) => manageFloatingIP.setFieldValue("ipAddress", e.target.value)}
                  onError={
                    manageFloatingIP.errors.ipAddress && manageFloatingIP.touched.ipAddress
                      ? manageFloatingIP.errors.ipAddress
                      : null
                  }
                />
              </div>
              {!isDeassociate && (
                <div className="grid gap-2">
                  <label className="text-sm font-medium">
                    Port to be associated <span className="text-red-500">*</span>
                  </label>
                  <Select
                    name="portId"
                    options={getPortOptions(selectedAssociateFloatingIP)}
                    placeholder="Select a port"
                    value={manageFloatingIP.values.portId}
                    onBlur={manageFloatingIP.handleBlur}
                    onChange={(e) => manageFloatingIP.setFieldValue("portId", e.target.value)}
                    onError={
                      manageFloatingIP.errors.portId && manageFloatingIP.touched.portId
                        ? manageFloatingIP.errors.portId
                        : null
                    }
                  />
                </div>
              )}
              <div className="flex justify-end gap-2">
                <button
                  onClick={isDeassociate ? toggleDeassociateFloatingIPModal : toggleFloatingIPModal}
                  className="px-4 py-4 text-xs border-[1.5px] border-primaryBlue rounded-sm text-primaryBlue cursor-pointer text-center"
                >
                  Cancel
                </button>
                <Button
                  buttonText={isDeassociate ? "De-associate" : "Associate"}
                  buttonRole={"submit"}
                />
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default FloatingIPS;